<template>
  <div class="game-form">

    <div class="game-form__tabs-shell">
    <v-tabs v-model="activeTab" background-color="transparent" class="game-form__tabs">
      <v-tab>
        <v-icon left small>mdi-controller-classic</v-icon>
        Game
      </v-tab>
      <v-tab :disabled="!item.id">
        <v-icon left small>mdi-note-text-outline</v-icon>
        Notes
      </v-tab>
      <v-tab :disabled="!item.id">
        <v-icon left small>mdi-source-branch</v-icon>
        Versions
      </v-tab>
      <v-tab :disabled="!item.id">
        <v-icon left small>mdi-rss</v-icon>
        Feeds
      </v-tab>
      <v-tab :disabled="!item.id">
        <v-icon left small>mdi-chart-timeline-variant</v-icon>
        Analytics
      </v-tab>
    </v-tabs>

    <!-- CSS transition (not v-tabs-items): transform only during enter/leave, so sticky Save works when idle. -->
    <transition name="game-tab-slide" mode="out-in">
    <div v-if="activeTab === 0" key="tab-game" class="game-form__tab-panel">
        <div v-if="isGameLoading" class="d-flex justify-center pa-8">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <div v-else class="game-form__tab-panel-body">
    <v-form class="mt-2" ref="form">
      <!-- Identity -->
      <v-card class="mb-4 game-form__section" elevation="1">
        <v-card-title class="d-flex align-center game-form__section-title">
          <v-icon class="mr-2">mdi-controller-classic</v-icon>
          <span class="font-weight-bold">Identity</span>
        </v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col v-if="item.id" cols="12" sm="2" md="2">
              <v-text-field :value="item.id" disabled dense hide-details>
                <template v-slot:label>
                  <field-help-label label="ID" field="id" />
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="5" :md="item.id ? 5 : 6">
              <v-text-field
                :rules="[rules.required]"
                v-model="item.slug"
                :disabled="!!item.id"
                required
                dense
                hide-details="auto"
              >
                <template v-slot:label>
                  <field-help-label label="Slug *" field="slug" />
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="5" :md="item.id ? 5 : 6">
              <v-text-field
                :rules="[rules.required]"
                v-model="item.name"
                required
                dense
                hide-details="auto"
              >
                <template v-slot:label>
                  <field-help-label label="Name *" field="name" />
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="12" md="2">
              <v-select v-model="item.tier" :items="tiers" clearable dense hide-details>
                <template v-slot:label>
                  <field-help-label label="Tier" field="tier" />
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" sm="12" md="5">
              <div class="game-form__field-with-action">
                <v-select
                  v-model="item.developerId"
                  :items="developers"
                  item-text="name"
                  item-value="id"
                  class="game-form__field-with-action-input"
                  :rules="[rules.required]"
                  required
                  dense
                  hide-details="auto"
                >
                  <template v-slot:label>
                    <field-help-label label="Developer *" field="developer" />
                  </template>
                </v-select>
                <v-btn
                  color="success"
                  outlined
                  small
                  class="game-form__field-with-action-btn"
                  @click="showCreateDeveloper = true"
                >
                  Create Developer
                </v-btn>
              </div>
            </v-col>
            <v-col cols="12" sm="12" md="5">
              <div class="game-form__field-with-action">
                <v-autocomplete
                  v-model="item.metaTemplateId"
                  :items="metaTemplates"
                  item-text="name"
                  item-value="id"
                  clearable
                  hide-no-data
                  class="game-form__field-with-action-input"
                  dense
                  hide-details
                >
                  <template v-slot:label>
                    <field-help-label label="Meta Template" field="metaTemplate" />
                  </template>
                </v-autocomplete>
                <v-btn
                  v-if="item.metaTemplateId"
                  color="primary"
                  outlined
                  small
                  class="game-form__field-with-action-btn"
                  @click="$router.push(`/metadata-management/${item.metaTemplateId}`)"
                >
                  Meta Management
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Tech stack -->
      <v-card class="mb-4 game-form__section" elevation="1">
        <v-card-title class="game-form__section-title">Tech stack</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-select
                :rules="[rules.required]"
                v-model="item.renderingType"
                :items="renderingTypes"
                item-text="name"
                item-value="value"
                required
                dense
                hide-details="auto"
              >
                <template v-slot:label>
                  <field-help-label label="Rendering Type *" field="renderingType" />
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="item.sdk" dense hide-details>
                <template v-slot:label>
                  <field-help-label label="SDK" field="sdk" />
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-combobox
                :rules="[rules.required]"
                v-model="item.engine"
                :items="engines"
                required
                dense
                hide-details="auto"
              >
                <template v-slot:label>
                  <field-help-label label="Engine *" field="engine" />
                </template>
              </v-combobox>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="item.engineVersion" dense hide-details>
                <template v-slot:label>
                  <field-help-label label="Engine Version" field="engineVersion" />
                </template>
              </v-text-field>
            </v-col>
            <v-col v-if="item.renderingType === 'JavaScript'" cols="12">
              <v-text-field v-model="item.sdkName" dense hide-details>
                <template v-slot:label>
                  <field-help-label label="Storage Path (deprecated)" field="sdkName" />
                </template>
              </v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Platform / auth / session -->
      <v-card class="mb-4 game-form__section" elevation="1">
        <v-card-title class="game-form__section-title">Platform / auth / session</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" sm="6" md="3">
              <v-switch v-model="item.hasAuth" class="mt-0" hide-details dense inset color="primary">
                <template v-slot:label>
                  <field-help-label label="Auth" field="hasAuth" />
                </template>
              </v-switch>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-switch v-model="item.hasEagle" class="mt-0" hide-details dense inset color="primary">
                <template v-slot:label>
                  <field-help-label label="Eagle" field="hasEagle" />
                </template>
              </v-switch>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Progress & persistence -->
      <v-card class="mb-4 game-form__section" elevation="1">
        <v-card-title class="game-form__section-title">Progress &amp; persistence</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" sm="6" md="4" lg="3">
              <v-switch v-model="item.hasInventory" class="mt-0" hide-details dense inset color="primary">
                <template v-slot:label>
                  <field-help-label label="Inventory" field="hasInventory" />
                </template>
              </v-switch>
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3">
              <v-switch v-model="item.hasCollection" class="mt-0" hide-details dense inset color="primary">
                <template v-slot:label>
                  <field-help-label label="Collection" field="hasCollection" />
                </template>
              </v-switch>
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3">
              <v-switch v-model="item.hasLeaderboard" class="mt-0" hide-details dense inset color="primary">
                <template v-slot:label>
                  <field-help-label label="Leaderboard" field="hasLeaderboard" />
                </template>
              </v-switch>
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3">
              <v-switch v-model="item.hasMultiplayer" class="mt-0" hide-details dense inset color="primary">
                <template v-slot:label>
                  <field-help-label label="Multiplayer" field="hasMultiplayer" />
                </template>
              </v-switch>
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="3">
              <v-switch v-model="item.hasPlatformGameEnd" class="mt-0" hide-details dense inset color="primary">
                <template v-slot:label>
                  <field-help-label label="Platform Game End" field="hasPlatformGameEnd" />
                </template>
              </v-switch>
            </v-col>
            <v-col cols="12" sm="4" md="3" lg="3" class="d-flex align-center">
              <v-switch v-model="item.hasCloudSave" class="mt-0" hide-details dense inset color="primary">
                <template v-slot:label>
                  <field-help-label label="Cloud Save" field="hasCloudSave" />
                </template>
              </v-switch>
            </v-col>
            <v-col cols="12" sm="8" md="5" lg="5">
              <v-text-field
                v-model="item.cloudSaveStorage"
                dense
                hide-details
                :disabled="!item.hasCloudSave"
              >
                <template v-slot:label>
                  <field-help-label label="Cloud Save Storage" field="cloudSaveStorage" />
                </template>
              </v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Monetization -->
      <v-card class="mb-4 game-form__section" elevation="1">
        <v-card-title class="game-form__section-title">Monetization (MTX / ads)</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" sm="6" md="4" lg="2">
              <v-switch v-model="item.hasGems" class="mt-0" hide-details dense inset color="primary">
                <template v-slot:label>
                  <field-help-label label="Gems" field="hasGems" />
                </template>
              </v-switch>
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="2">
              <v-switch v-model="item.hasInterstitialAds" class="mt-0" hide-details dense inset color="primary">
                <template v-slot:label>
                  <field-help-label label="Interstitial Ads" field="hasInterstitialAds" />
                </template>
              </v-switch>
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="2">
              <v-switch v-model="item.hasRewardedAds" class="mt-0" hide-details dense inset color="primary">
                <template v-slot:label>
                  <field-help-label label="Rewarded Ads" field="hasRewardedAds" />
                </template>
              </v-switch>
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="2">
              <v-switch v-model="item.isAdsFree" class="mt-0" hide-details dense inset color="primary">
                <template v-slot:label>
                  <field-help-label label="Ads Free" field="isAdsFree" />
                </template>
              </v-switch>
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="2">
              <v-switch v-model="item.adPodding" class="mt-0" hide-details dense inset color="primary">
                <template v-slot:label>
                  <field-help-label label="Ad Podding" field="adPodding" />
                </template>
              </v-switch>
            </v-col>
            <v-col cols="12" md="8">
              <v-text-field v-model="item.mtxDetails" dense hide-details>
                <template v-slot:label>
                  <field-help-label label="MTX Details" field="mtxDetails" />
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="item.adLayoutCode" dense hide-details>
                <template v-slot:label>
                  <field-help-label label="AD Layout Code" field="adLayoutCode" />
                </template>
              </v-text-field>
            </v-col>
          </v-row>

          <div class="game-form__nested mt-2">
            <div class="game-form__nested-title d-flex align-center">
              <span>Game Preroll Settings</span>
              <v-tooltip v-if="showPrerollHistory" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    class="ml-1"
                    color="secondary"
                    tag="a"
                    :href="prerollChangeLogHref"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="game-form-preroll-history"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon>mdi-history</v-icon>
                  </v-btn>
                </template>
                <span>View change history</span>
              </v-tooltip>
            </div>
            <v-row dense>
              <v-col cols="12" md="3">
                <v-select
                  :items="prerollStatusItems"
                  v-model="prerollEnabledStatus"
                  item-text="text"
                  item-value="value"
                  dense
                  hide-details
                >
                  <template v-slot:label>
                    <field-help-label label="Preroll status" field="gamePrerollSettings.isEnabled" />
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-text-field
                  v-model="gamePrerollSettings.freePeriod"
                  type="number"
                  min="0"
                  dense
                  hide-details
                  :disabled="prerollEnabledStatus !== 'enabled'"
                >
                  <template v-slot:label>
                    <field-help-label label="Free Period (days)" field="gamePrerollSettings.freePeriod" />
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="gamePrerollSettings.seconds"
                  type="number"
                  min="0"
                  dense
                  hide-details
                  :disabled="prerollEnabledStatus !== 'enabled'"
                >
                  <template v-slot:label>
                    <field-help-label label="Duration (sec)" field="gamePrerollSettings.seconds" />
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-text-field
                  v-model="gamePrerollSettings.firstStartRate"
                  type="number"
                  min="0"
                  max="100"
                  dense
                  hide-details
                  :disabled="prerollEnabledStatus !== 'enabled'"
                >
                  <template v-slot:label>
                    <field-help-label label="First Start Rate" field="gamePrerollSettings.firstStartRate" />
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-text-field
                  v-model="gamePrerollSettings.showRate"
                  type="number"
                  min="0"
                  max="100"
                  dense
                  hide-details
                  :disabled="prerollEnabledStatus !== 'enabled'"
                >
                  <template v-slot:label>
                    <field-help-label label="Show Rate" field="gamePrerollSettings.showRate" />
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
      </v-card>

      <!-- Rendering & layout -->
      <v-card class="mb-4 game-form__section" elevation="1">
        <v-card-title class="game-form__section-title">Rendering &amp; layout</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" sm="6" md="4" lg="2">
              <v-switch v-model="item.isGRM" class="mt-0" hide-details dense inset color="primary">
                <template v-slot:label>
                  <field-help-label label="Rendering in GRM" field="isGRM" />
                </template>
              </v-switch>
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="2">
              <v-switch v-model="item.isIframe" class="mt-0" hide-details dense inset color="primary">
                <template v-slot:label>
                  <field-help-label label="Rendering in IFrame" field="isIframe" />
                </template>
              </v-switch>
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="2">
              <v-switch v-model="item.isFlex" class="mt-0" hide-details dense inset color="primary">
                <template v-slot:label>
                  <field-help-label label="Flexible Size" field="isFlex" />
                </template>
              </v-switch>
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="2">
              <v-switch v-model="item.hasFullscreen" class="mt-0" hide-details dense inset color="primary">
                <template v-slot:label>
                  <field-help-label label="Full Screen" field="hasFullscreen" />
                </template>
              </v-switch>
            </v-col>
            <v-col cols="12" sm="6" md="4" lg="2">
              <v-switch v-model="item.hasPortraitMode" class="mt-0" hide-details dense inset color="primary">
                <template v-slot:label>
                  <field-help-label label="Portrait Mode" field="hasPortraitMode" />
                </template>
              </v-switch>
            </v-col>
            <v-col cols="12">
              <v-row dense align="center">
                <v-col cols="12" sm="4" md="2" lg="2">
                  <v-select
                    v-model="item.aspectRatio"
                    :items="aspectRatios"
                    item-text="name"
                    item-value="value"
                    dense
                    hide-details
                  >
                    <template v-slot:label>
                      <field-help-label label="Aspect Ratio" field="aspectRatio" />
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" sm="8" md="8" lg="8">
                  <v-text-field
                    v-model="item.iframeAllowOptions"
                    placeholder="web-share; camera; microphone; geolocation"
                    dense
                    hide-details
                    @input="parseIframeAllowOptions"
                  >
                    <template v-slot:label>
                      <field-help-label label="Iframe Allow Options" field="iframeAllowOptions" />
                    </template>
                  </v-text-field>
                </v-col>
                <v-col cols="12" sm="12" md="2" lg="2" class="d-flex align-center">
                  <v-btn
                    color="primary"
                    outlined
                    small
                    @click="showPermissionsDialog = true"
                  >
                    Available Directives
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Availability -->
      <v-card class="mb-4 game-form__section" elevation="1">
        <v-card-title class="game-form__section-title">Availability</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" sm="6" md="3">
              <v-switch v-model="item.hasComingSoon" class="mt-0" hide-details dense inset color="primary">
                <template v-slot:label>
                  <field-help-label label="Coming Soon" field="hasComingSoon" />
                </template>
              </v-switch>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field type="date" v-model="item.comingSoonDate" dense hide-details>
                <template v-slot:label>
                  <field-help-label label="Coming Soon Date" field="comingSoonDate" />
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-switch v-model="item.isSubscriberOnly" class="mt-0" hide-details dense inset color="primary">
                <template v-slot:label>
                  <field-help-label label="Subscriber Only" field="isSubscriberOnly" />
                </template>
              </v-switch>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field type="date" v-model="item.isSubscriberOnlyDate" dense hide-details>
                <template v-slot:label>
                  <field-help-label label="Subscriber Only Date" field="isSubscriberOnlyDate" />
                </template>
              </v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- supportInfo -->
      <v-card class="mb-4 game-form__section" elevation="1">
        <v-card-title class="game-form__section-title d-flex align-center">
          <span>Support Info</span>
          <v-tooltip v-if="showSupportInfoHistory" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                class="ml-1"
                color="secondary"
                tag="a"
                :href="supportInfoChangeLogHref"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="game-form-support-info-history"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-history</v-icon>
              </v-btn>
            </template>
            <span>View change history</span>
          </v-tooltip>
        </v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" sm="4" md="2" lg="2">
              <v-select
                :items="supportInfoStatusItems"
                v-model="supportInfoActiveStatus"
                item-text="text"
                item-value="value"
                dense
                hide-details
              >
                <template v-slot:label>
                  <field-help-label label="Status" field="supportInfo.isEnabled" />
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" sm="8" md="10" lg="10">
              <v-text-field
                v-model="supportInfo.description"
                dense
                hide-details
                :disabled="supportInfoActiveStatus !== 'enabled'"
              >
                <template v-slot:label>
                  <field-help-label label="Description" field="supportInfo.description" />
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="supportInfo.emailText"
                dense
                hide-details
                :disabled="supportInfoActiveStatus !== 'enabled'"
              >
                <template v-slot:label>
                  <field-help-label label="Email Text" field="supportInfo.emailText" />
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="supportInfo.email"
                dense
                hide-details
                :disabled="supportInfoActiveStatus !== 'enabled'"
              >
                <template v-slot:label>
                  <field-help-label label="Email" field="supportInfo.email" />
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="supportInfo.phoneText"
                dense
                hide-details
                :disabled="supportInfoActiveStatus !== 'enabled'"
              >
                <template v-slot:label>
                  <field-help-label label="Phone Text" field="supportInfo.phoneText" />
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-text-field
                v-model="supportInfo.phone"
                dense
                hide-details
                :disabled="supportInfoActiveStatus !== 'enabled'"
              >
                <template v-slot:label>
                  <field-help-label label="Phone" field="supportInfo.phone" />
                </template>
              </v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Live ops -->
      <v-card class="mb-4 game-form__section" elevation="1">
        <v-card-title class="game-form__section-title">Live ops</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12">
              <v-text-field v-model="item.liveOpsDependencies" dense hide-details>
                <template v-slot:label>
                  <field-help-label label="Live Ops Dependencies" field="liveOpsDependencies" />
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="item.webhookDev" dense hide-details>
                <template v-slot:label>
                  <field-help-label label="Webhook URL (Dev)" field="webhookDev" />
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="item.webhookLive" dense hide-details>
                <template v-slot:label>
                  <field-help-label label="Webhook URL (Live)" field="webhookLive" />
                </template>
              </v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <div class="game-form__save mb-2">
        <v-btn color="primary" large class="mr-2" data-testid="game-form-save" @click="save()">Save</v-btn>
        <v-btn outlined large class="mr-2" :to="'/games'" data-testid="game-form-cancel">Cancel</v-btn>
        <v-btn
          v-if="item.id"
          outlined
          large
          color="secondary"
          :to="changeLogLink"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="game-form-change-history"
        >
          <v-icon left>mdi-history</v-icon>
          History
        </v-btn>
      </div>
    </v-form>
        </div>
    </div>

    <div v-else-if="activeTab === 1" key="tab-notes" class="game-form__tab-panel">
        <div
          class="game-form__tab-panel-body"
          v-if="item.id && visitedTabs[1]"
        >
          <GameNotes :game="item" />
        </div>
        <div v-else-if="isGameLoading || !visitedTabs[1]" class="d-flex justify-center pa-8">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <v-alert v-else-if="!item.id && !isGameLoading" type="info" dense outlined class="ma-4">
          Save the game first to manage notes.
        </v-alert>
    </div>

    <div v-else-if="activeTab === 2" key="tab-versions" class="game-form__tab-panel">
        <div class="game-form__tab-panel-body" v-if="item.id && visitedTabs[2]">
          <VersionsList :game="item" :items="item.versions" @update="onFeedsUpdate" />
        </div>
        <div v-else-if="isGameLoading || !visitedTabs[2]" class="d-flex justify-center pa-8">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <v-alert v-else-if="!item.id && !isGameLoading" type="info" dense outlined class="ma-4">
          Save the game first to manage versions.
        </v-alert>
    </div>

    <div v-else-if="activeTab === 3" key="tab-feeds" class="game-form__tab-panel">
        <div class="game-form__tab-panel-body" v-if="item.id && visitedTabs[3] && feedsCatalogLoaded">
          <FeedsList :game="item" :items="item.feedGames" :feeds="feedsCatalog" @update="onFeedsUpdate" />
        </div>
        <div
          v-else-if="isGameLoading || feedsCatalogLoading || !visitedTabs[3] || !feedsCatalogLoaded"
          class="d-flex justify-center pa-8"
        >
          <v-progress-circular indeterminate color="primary" />
        </div>
        <v-alert v-else-if="!item.id && !isGameLoading" type="info" dense outlined class="ma-4">
          Save the game first to manage feed placements.
        </v-alert>
    </div>

    <div v-else-if="activeTab === 4" key="tab-analytics" class="game-form__tab-panel">
        <div
          class="game-form__tab-panel-body game-form__tab-panel-body--padded"
          v-if="item.id && visitedTabs[4]"
        >
          <GameAnalyticsTab :game="item" />
        </div>
        <div v-else-if="isGameLoading || !visitedTabs[4]" class="d-flex justify-center pa-8">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <v-alert v-else-if="!item.id && !isGameLoading" type="info" dense outlined class="ma-4">
          Save the game first to attach analytics configs.
        </v-alert>
    </div>
    </transition>
    </div>

    <!-- Create Developer Dialog -->
    <v-dialog v-model="showCreateDeveloper" max-width="640" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <span class="font-weight-bold">Create Developer</span>
          <v-spacer />
          <v-btn icon small @click="closeCreateDeveloperDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-2">
          <v-card class="mb-0 game-form__section" elevation="1">
            <v-card-text>
              <v-row dense>
                <v-col cols="12">
                  <v-text-field
                    v-model="newDeveloper.name"
                    label="Name *"
                    :rules="[rules.required]"
                    required
                    dense
                    hide-details="auto"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="newDeveloper.azureAdId"
                    label="Azure Ad Id (studio name) *"
                    :rules="[rules.required]"
                    required
                    dense
                    hide-details="auto"
                  />
                </v-col>
              </v-row>
              <div class="d-flex justify-end mt-4">
                <v-btn color="primary" class="mr-2" :disabled="!canCreateDeveloper" @click="createDeveloper">
                  Save
                </v-btn>
                <v-btn outlined @click="closeCreateDeveloperDialog">Cancel</v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Permissions Dialog -->
    <v-dialog v-model="showPermissionsDialog" max-width="800" persistent>
      <v-card>
        <v-card-title>
          Available Permissions-Policy Directives
          <v-spacer></v-spacer>
          <v-btn icon @click="closePermissionsDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col v-for="directive in permissionsPolicyDirectives" :key="directive.name" cols="12" sm="6" md="4">
              <v-checkbox
                v-model="selectedDirectives"
                :value="directive.name"
                :label="directive.name"
                dense
                hide-details
                class="ma-0 pa-0"
                @change="updateIframeAllowOptions"
              >
                <template v-slot:append>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon x-small v-bind="attrs" v-on="on" class="ml-1">mdi-information</v-icon>
                    </template>
                    <span>{{ directive.description }}</span>
                  </v-tooltip>
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="closePermissionsDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

<!-- Alias Management Dialog -->
    <v-dialog v-model="showAliasManagement" width="1200" persistent>
      <v-card>
        <v-card-title>
          Alias Management
          <v-spacer />
          <v-btn @click="closeAliasManagementDialog">Close</v-btn>
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="aliasSearch"
            prepend-icon="mdi-magnify"
            label="Search"
            class="mb-4"
          ></v-text-field>
          <div class="d-flex align-center mb-4">
            <v-text-field
              v-model="bulkAliasValue"
              label="Bulk update alias"
              class="mr-4"
            ></v-text-field>
            <v-btn
              color="primary"
              @click="updateSelectedAliases"
              :disabled="!bulkAliasValue || (selectedFeedGames.length === 0 && selectedGameAliases.length === 0)"
              class="mr-2"
            >
              Update Selected
            </v-btn>
            <v-btn
              color="error"
              @click="clearSelectedAliases"
              :disabled="selectedFeedGames.length === 0 && selectedGameAliases.length === 0"
            >
              Clear Selected
            </v-btn>
          </div>

          <!-- Game Level Aliases -->
          <v-card class="mb-4">
            <v-card-title>Game Level Aliases</v-card-title>
            <v-data-table
              v-model="selectedGameAliases"
              :headers="aliasHeaders"
              :items="gameAliases"
              :items-per-page="15"
              show-select
            >
              <template v-for="locale in gameLocales" v-slot:[`item.alias_${locale.value}`]="{ item }">
                <span>
                  {{ item[`alias_${locale.value}`] }}
                </span>
              </template>
            </v-data-table>
          </v-card>

          <!-- Feed Level Aliases -->
          <v-card>
            <v-card-title>Feed Level Aliases</v-card-title>
            <v-data-table
              v-model="selectedFeedGames"
              :headers="aliasHeaders"
              :items="feedAliases"
              :items-per-page="15"
              :search="aliasSearch"
              :custom-filter="filterFeedGames"
              show-select
            >
              <template v-slot:item.feed="{ item }">
                <span>
                  {{ item.feed ? `${item.feed.name} (${item.feed.environment})` : 'Game Level' }}
                </span>
              </template>
              <template v-for="locale in gameLocales" v-slot:[`item.alias_${locale.value}`]="{ item }">
                <span>
                  {{ item[`alias_${locale.value}`] }}
                </span>
              </template>
            </v-data-table>
          </v-card>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Help Dialog -->
    <v-dialog v-model="showHelp" width="800" persistent>
      <v-card>
        <v-card-title>
          Alias Management Help
          <v-spacer />
          <v-btn @click="closeHelpDialog">Close</v-btn>
        </v-card-title>
        <v-card-text>
          <h3 class="mb-4">Overview</h3>
          <p class="mb-6">The Alias Management feature allows you to manage game aliases across different languages and feeds in one place. Aliases are used in URLs on platforms instead of slugs.</p>

          <h3 class="mb-4">How Aliases Work</h3>
          <ul class="mb-6">
            <li>Game-level aliases serve as fallbacks for feeds where the alias is missing</li>
            <li>Feed-level aliases take precedence over game-level aliases</li>
            <li>Updates only apply to existing meta entries</li>
          </ul>

          <h3 class="mb-4">Interface</h3>
          <p class="mb-2">Two tables show aliases at different levels:</p>
          <ul class="mb-6">
            <li><strong>Game Level</strong> - Global aliases for all feeds</li>
            <li><strong>Feed Level</strong> - Feed-specific aliases</li>
          </ul>

          <h4 class="mb-2">Search</h4>
          <p class="mb-6">Search across feed names and any language's alias.</p>

          <h4 class="mb-2">Bulk Updates</h4>
          <ol class="mb-6">
            <li>Select rows from either table</li>
            <li>Enter the new alias value</li>
            <li>Click "Update Selected"</li>
          </ol>

          <h4 class="mb-2">Clearing Aliases</h4>
          <ol class="mb-6">
            <li>Select rows from either table</li>
            <li>Click "Clear Selected" to remove aliases (set to null)</li>
          </ol>

          <h3 class="mb-4">Tips</h3>
          <ol>
            <li>Verify selections before updating</li>
            <li>Use search to find specific feeds/aliases</li>
            <li>Missing translations show as "-"</li>
          </ol>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import BasicTemplate from '../../templates/basic.vue';
import Versions from './versions/';
import Feeds from './feeds/';
import GameNotes from '../../components/GameNotes/GameNotes.vue';
import FieldHelpLabel from '../../components/FieldHelpLabel/FieldHelpLabel.vue';
import GameAnalyticsTab from './analytics/GameAnalyticsTab.vue';
import { gameLocales } from '../../constants/locales';
import persistentDialogEsc from '@/mixins/persistentDialogEsc';

const GAME_TABS = ['game', 'notes', 'versions', 'feeds', 'analytics'];

function tabIndexFromQuery(tab, allowSecondaryTabs) {
  const key = String(tab || 'game').toLowerCase();
  const index = GAME_TABS.indexOf(key);
  if (index <= 0) return 0;
  return allowSecondaryTabs ? index : 0;
}

function tabNameFromIndex(index) {
  return GAME_TABS[index] || 'game';
}

export default {
  name: 'GameForm',
  components: {
    BasicTemplate,
    VersionsList: Versions.List,
    FeedsList: Feeds.List,
    GameNotes,
    FieldHelpLabel,
    GameAnalyticsTab,
  },
  mixins: [
    persistentDialogEsc({
      showCreateDeveloper: 'closeCreateDeveloperDialog',
      showPermissionsDialog: 'closePermissionsDialog',
      showAliasManagement: 'closeAliasManagementDialog',
      showHelp: 'closeHelpDialog',
    }),
  ],
  props: {
    item: {}
  },
  created() {
    this.syncTabFromRoute();
    this.markTabVisited(this.activeTab);
    this.loadDevelopers();
    this.loadMetaTemplates();
    this.aliasHeaders = [
      { text: 'Source', value: 'feed', sortable: false },
      ...gameLocales.map(locale => ({
        text: locale.value.toUpperCase(),
        value: `alias_${locale.value}`,
        sortable: false
      }))
    ];
  },
  data() {
    return {
      showAliasManagement: false,
      showHelp: false,
      showPermissionsDialog: false,
      showCreateDeveloper: false,
      activeTab: 0,
      visitedTabs: { 0: true },
      feedsCatalog: [],
      feedsCatalogLoaded: false,
      feedsCatalogLoading: false,
      newDeveloper: { name: '', azureAdId: '' },
      gameLocales,
      selectedFeedGames: [],
      selectedGameAliases: [],
      aliasSearch: '',
      bulkAliasValue: '',
      aliasHeaders: [],
      gameAliases: [],
      feedAliases: [],
      developers: [],
      metaTemplates: [],
      engines: ['HTML', 'Core', 'Unity', 'Construct', 'Other', 'Defold', 'Godot'].sort(),
      tiers: ['S', 'A', 'B', 'C', 'D'],
      aspectRatios: [
        {value: null, name: 'Not Set'},
        {value: 4 / 3, name: '4:3'},
        {value: 16 / 9, name: '16:9'},
      ],
      renderingTypes: [
        {value: 'Html', name: 'Standard (index.html)'},
        {value: 'JavaScript', name: 'Old (phoenixcfg.json)'},
        {value: 'External', name: 'External (URL to other website)'},
      ],
      selectedDirectives: [],
      permissionsPolicyDirectives: [
        { name: 'web-share', description: 'Controls access to the Web Share API' },
        { name: 'camera', description: 'Controls access to camera devices' },
        { name: 'microphone', description: 'Controls access to microphone devices' },
        { name: 'geolocation', description: 'Controls access to geolocation information' },
        { name: 'payment', description: 'Controls access to Payment Request API' },
        { name: 'usb', description: 'Controls access to USB devices' },
        { name: 'magnetometer', description: 'Controls access to magnetometer sensors' },
        { name: 'gyroscope', description: 'Controls access to gyroscope sensors' },
        { name: 'accelerometer', description: 'Controls access to accelerometer sensors' },
        { name: 'ambient-light-sensor', description: 'Controls access to ambient light sensors' },
        { name: 'autoplay', description: 'Controls autoplay of media' },
        { name: 'battery', description: 'Controls access to battery status information' },
        { name: 'bluetooth', description: 'Controls access to Bluetooth devices' },
        { name: 'cross-origin-isolated', description: 'Controls cross-origin isolation features' },
        { name: 'display-capture', description: 'Controls access to screen capture' },
        { name: 'document-domain', description: 'Controls document.domain setter' },
        { name: 'encrypted-media', description: 'Controls access to Encrypted Media Extensions' },
        { name: 'execution-while-not-rendered', description: 'Controls execution while not rendered' },
        { name: 'execution-while-out-of-viewport', description: 'Controls execution while out of viewport' },
        { name: 'fullscreen', description: 'Controls access to fullscreen API' },
        { name: 'gamepad', description: 'Controls access to Gamepad API' },
        { name: 'hid', description: 'Controls access to Human Interface Devices' },
        { name: 'idle-detection', description: 'Controls access to Idle Detection API' },
        { name: 'keyboard-map', description: 'Controls access to keyboard mapping' },
        { name: 'midi', description: 'Controls access to MIDI devices' },
        { name: 'navigation-override', description: 'Controls navigation override' },
        { name: 'picture-in-picture', description: 'Controls Picture-in-Picture API' },
        { name: 'publickey-credentials-get', description: 'Controls access to WebAuthn API' },
        { name: 'screen-wake-lock', description: 'Controls access to Screen Wake Lock API' },
        { name: 'serial', description: 'Controls access to Serial API' },
        { name: 'speaker-selection', description: 'Controls access to speaker selection' },
        { name: 'sync-xhr', description: 'Controls synchronous XMLHttpRequest' },
        { name: 'trust-token-redemption', description: 'Controls Trust Token redemption' },
        { name: 'xr-spatial-tracking', description: 'Controls access to WebXR spatial tracking' }
      ],
      rules: {
        required: (value) => !!(value != null && String(value).trim()) || 'Required.',
      },
      prerollStatusItems: [
        { text: 'Not set', value: 'not_set' },
        { text: 'Enabled', value: 'enabled' },
        { text: 'Disabled', value: 'disabled' }
      ],
      supportInfoStatusItems: [
        { text: 'Not set', value: 'not_set' },
        { text: 'Enabled', value: 'enabled' },
        { text: 'Disabled', value: 'disabled' }
      ],
    };
  },
  computed: {
    isGameLoading() {
      return !!this.$route.params.id && !this.item?.id;
    },
    changeLogLink() {
      return {
        path: '/game-data-change-log',
        query: {
          table: 'game',
          entityId: String(this.item.id),
        },
      };
    },
    showPrerollHistory() {
      return !!this.item?.id && !!this.item.gamePrerollSettings && this.item.__prerollWasNull !== true;
    },
    prerollChangeLogLink() {
      return {
        path: '/game-data-change-log',
        query: {
          table: 'game_preroll_settings',
          entityId: String(this.item.id),
        },
      };
    },
    prerollChangeLogHref() {
      return this.$router.resolve(this.prerollChangeLogLink).href;
    },
    showSupportInfoHistory() {
      return !!this.item?.id && !!this.item.supportInfo && this.item.__supportInfoWasNull !== true;
    },
    supportInfoChangeLogLink() {
      return {
        path: '/game-data-change-log',
        query: {
          table: 'game_support_info',
          entityId: String(this.item.id),
        },
      };
    },
    supportInfoChangeLogHref() {
      return this.$router.resolve(this.supportInfoChangeLogLink).href;
    },
    canCreateDeveloper() {
      return !!this.newDeveloper.name && !!this.newDeveloper.azureAdId;
    },
    gamePrerollSettings() {
      if (!this.item.gamePrerollSettings) {
        // Remember that the API returned null originally
        if (this.item.__prerollWasNull === undefined) {
          this.$set(this.item, '__prerollWasNull', true);
        }
        this.$set(this.item, 'gamePrerollSettings', {
          isEnabled: null,
          freePeriod: null,
          seconds: null,
          firstStartRate: null,
          showRate: null
        });
      }
      return this.item.gamePrerollSettings;
    },
    prerollEnabledStatus: {
      get() {
        // Ensure settings initialization (also sets __prerollWasNull when needed)
        const s = this.gamePrerollSettings;
        if (this.item.__prerollWasNull || s.isEnabled === null) return 'not_set';
        return s.isEnabled ? 'enabled' : 'disabled';
      },
      set(val) {
        if (val === 'not_set') {
          this.$set(this.item, '__prerollWasNull', true);
          // Keep object for binding but mark disabled and clear values
          const s = this.gamePrerollSettings;
          s.isEnabled = null;
          s.freePeriod = null;
          s.seconds = null;
          s.firstStartRate = null;
          s.showRate = null;
        } else {
          this.$set(this.item, '__prerollWasNull', false);
          const enabled = val === 'enabled';
          this.gamePrerollSettings.isEnabled = enabled;
        }

      }
    },
    supportInfo() {
      if (!this.item.supportInfo) {
        // Remember that the API returned null originally
        if (this.item.__supportInfoWasNull === undefined) {
          this.$set(this.item, '__supportInfoWasNull', true);
        }
        this.$set(this.item, 'supportInfo', {
          active: null,
          description: null,
          emailText: null,
          email: null,
          phoneText: null,
          phone: null
        });
      }
      return this.item.supportInfo;
    },
    supportInfoActiveStatus: {
      get() {
        // Ensure settings initialization (also sets __supportInfoWasNull when needed)
        const s = this.supportInfo;
        if (this.item.__supportInfoWasNull || s.active === null) return 'not_set';
        return s.active ? 'enabled' : 'disabled';
      },
      set(val) {
        if (val === 'not_set') {
          this.$set(this.item, '__supportInfoWasNull', true);
          // Keep object for binding but mark unset and clear values
          const s = this.supportInfo;
          s.active = null;
          s.description = null;
          s.emailText = null;
          s.email = null;
          s.phoneText = null;
          s.phone = null;
        } else {
          this.$set(this.item, '__supportInfoWasNull', false);
          this.supportInfo.active = val === 'enabled';
        }
      }
    }
  },
  watch: {
    '$route.query.tab'() {
      this.syncTabFromRoute();
    },
    activeTab(index) {
      this.markTabVisited(index);
      this.syncTabToRoute(index);
    },
    showAliasManagement(newVal) {
      if (newVal && this.item.id) {
        this.ensureFeedsLoaded().then(() => this.loadFeedAliases());
      }
    },
    'item.id': {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadMetaTemplates();
          this.syncTabFromRoute();
          this.markTabVisited(this.activeTab);
        } else if (!this.$route.params.id && this.activeTab !== 0) {
          // New game page only — keep deep-linked tabs while edit page loads
          this.activeTab = 0;
        }
        if (newVal && this.showAliasManagement) {
          this.ensureFeedsLoaded().then(() => this.loadFeedAliases());
        }
      }
    },
    'item.iframeAllowOptions': {
      immediate: true,
      handler() {
        this.parseIframeAllowOptions();
      }
    },
    item: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal) {
          // Force computed property to re-evaluate
          this.$forceUpdate();
        }
      }
    }
  },
  methods: {
    closeCreateDeveloperDialog() {
      this.showCreateDeveloper = false;
    },
    closePermissionsDialog() {
      this.showPermissionsDialog = false;
    },
    closeAliasManagementDialog() {
      this.showAliasManagement = false;
    },
    closeHelpDialog() {
      this.showHelp = false;
    },
    markTabVisited(index) {
      if (!this.visitedTabs[index]) {
        this.$set(this.visitedTabs, index, true);
      }
      if (index === 3) {
        this.ensureFeedsLoaded();
      }
    },
    async ensureFeedsLoaded() {
      if (this.feedsCatalogLoaded || this.feedsCatalogLoading) return;
      this.feedsCatalogLoading = true;
      try {
        this.feedsCatalog = await this.$gameApi.get('/feeds') || [];
        this.feedsCatalogLoaded = true;
      } catch (e) {
        this.feedsCatalog = [];
        this.$swal.fire({
          icon: 'error',
          title: e.message || 'Failed to load feeds',
          timer: 1500,
          showConfirmButton: false
        });
      } finally {
        this.feedsCatalogLoading = false;
      }
    },
    syncTabFromRoute() {
      // Prefer route param so deep links work before game item finishes loading
      const allowSecondaryTabs = !!(this.item?.id || this.$route.params.id);
      const next = tabIndexFromQuery(this.$route.query.tab, allowSecondaryTabs);
      if (this.activeTab !== next) {
        this.activeTab = next;
      } else {
        this.markTabVisited(next);
      }
    },
    syncTabToRoute(index) {
      const allowSecondaryTabs = !!(this.item?.id || this.$route.params.id);
      if (!allowSecondaryTabs && index !== 0) {
        this.activeTab = 0;
        return;
      }
      const tab = tabNameFromIndex(index);
      const current = String(this.$route.query.tab || 'game').toLowerCase();
      if (current === tab) return;
      this.$router.replace({
        query: {
          ...this.$route.query,
          tab
        }
      }).catch(() => {});
    },
    async onFeedsUpdate() {
      // Reload game data after feeds update
      if (this.item?.id) {
        this.item = await this.$gameApi.get(`/games/${this.item.id}`);
      }
    },
    async loadDevelopers() {
      this.developers = await this.$gameApi.get(`/users`);
      if (!this.item.developerId) {
        const ark = this.developers.find(d => d.name === 'Arkadium');
        if (ark) {
          this.item.developerId = ark.id;
        }
      }
    },
    async loadMetaTemplates() {
      try {
        const gameId = this.item?.id;
        const query = gameId ? `?supportedGameId=${gameId}` : '';
        const templates = await this.$gameApi.get(`/meta-templates${query}`);
        const list = Array.isArray(templates) ? templates : (templates?.items || templates?.data || []);
        this.metaTemplates = list
          .slice()
          .sort((a, b) => (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' }));
      } catch (e) {
        this.metaTemplates = [];
      }
    },
    async createDeveloper() {
      try {
        const body = { name: this.newDeveloper.name, azureAdId: this.newDeveloper.azureAdId };
        const created = await this.$gameApi.post(`/users/`, body);
        // Refresh list and select created developer
        this.developers = await this.$gameApi.get(`/users`);
        this.item.developerId = created.id;
        this.$swal.fire({ icon: 'success', title: 'Developer created', timer: 1500, showConfirmButton: false });
        this.showCreateDeveloper = false;
        this.newDeveloper = { name: '', azureAdId: '' };
      } catch (e) {
        this.$swal.fire({ icon: 'error', title: e.message || 'Error creating developer', timer: 2000, showConfirmButton: false });
      }
    },
    async loadFeedAliases() {
      if (!this.item.id) {
        return;
      }

      try {
        // Load game level aliases
        const gameMetas = await this.$gameApi.get(`/games/${this.item.id}/metas`);

        const gameLocaleAliases = {};
        gameLocales.forEach(locale => {
          const meta = gameMetas.find(m => m.locale === locale.value);
          gameLocaleAliases[`alias_${locale.value}`] = meta ? meta.alias : '-';
        });

        // Set game level aliases
        this.gameAliases = [{
          id: 'game-level',
          feed: null,
          ...gameLocaleAliases,
          hasEnMeta: !!gameMetas.find(m => m.locale === 'en'),
          isSelectable: !!gameMetas.find(m => m.locale === 'en')
        }];

        // Load feed level aliases
        if (!this.item.feedGames || !Array.isArray(this.item.feedGames)) {
          this.feedAliases = [];
          return;
        }

        const feedGames = await Promise.all(
          this.item.feedGames.map(async (fg) => {
            try {
              const metas = await this.$gameApi.get(`/feeds/${fg.feedId}/games/${this.item.id}/metas`);
              const feed = this.feedsCatalog.find(f => f.id === fg.feedId);

              const localeAliases = {};
              gameLocales.forEach(locale => {
                const meta = metas.find(m => m.locale === locale.value);
                localeAliases[`alias_${locale.value}`] = meta ? meta.alias : '-';
              });

              return {
                ...fg,
                feed,
                ...localeAliases,
                hasEnMeta: !!metas.find(m => m.locale === 'en')
              };
            } catch (e) {
              console.error(`Error loading metas for feed ${fg.feedId}:`, e);
              return null;
            }
          })
        );

        // Filter out any null results from failed feed loads
        this.feedAliases = feedGames.filter(fg => fg !== null);
      } catch (e) {
        console.error('Error loading aliases:', e);
        this.$swal.fire({
          icon: 'error',
          title: 'Error loading aliases',
          text: e.message || 'An unknown error occurred',
          timer: 2500,
          showConfirmButton: false
        });
      }
    },
    async updateSelectedAliases() {
      try {
        // Update selected game level aliases
        if (this.selectedGameAliases.length > 0) {
          const metas = await this.$gameApi.get(`/games/${this.item.id}/metas`) || [];

          // Update or create meta for each locale
          const fallbackMeta = metas.find(m => m.locale === 'en') || metas[0] || {};
          await Promise.all(
            gameLocales.map(async (locale) => {
              const existingMeta = metas.find(m => m.locale === locale.value);

              if (existingMeta) {
                // Update existing meta
                await this.$gameApi.patch(`/games/${this.item.id}/metas/${existingMeta.id}`, {
                  ...existingMeta,
                  alias: this.bulkAliasValue
                });
              } else {
                // Create new meta for this locale
                await this.$gameApi.post(`/games/${this.item.id}/metas`, {
                  locale: locale.value,
                  alias: this.bulkAliasValue,
                  name: fallbackMeta.name || '',
                  description: fallbackMeta.description || '',
                  rules: fallbackMeta.rules || '',
                  htmlTitle: fallbackMeta.htmlTitle || '',
                  htmlDescription: fallbackMeta.htmlDescription || '',
                  categories: [] // Categories are locale-specific, so start with empty array
                });
              }
            })
          );
        }

        // Update selected feed level aliases
        if (this.selectedFeedGames.length > 0) {
          await Promise.all(
            this.selectedFeedGames.map(async (item) => {
              const metas = await this.$gameApi.get(`/feeds/${item.feedId}/games/${this.item.id}/metas`) || [];

              // Update or create meta for each locale
              const fallbackMeta = metas.find(m => m.locale === 'en') || metas[0] || {};
              await Promise.all(
                gameLocales.map(async (locale) => {
                  const existingMeta = metas.find(m => m.locale === locale.value);

                  if (existingMeta) {
                    // Update existing meta
                    await this.$gameApi.patch(`/feeds/${item.feedId}/games/${this.item.id}/metas/${existingMeta.id}`, {
                      ...existingMeta,
                      alias: this.bulkAliasValue
                    });
                  } else {
                    // Create new meta for this locale
                    await this.$gameApi.post(`/feeds/${item.feedId}/games/${this.item.id}/metas`, {
                      locale: locale.value,
                      alias: this.bulkAliasValue,
                      name: fallbackMeta.name || '',
                      description: fallbackMeta.description || '',
                      rules: fallbackMeta.rules || '',
                      htmlTitle: fallbackMeta.htmlTitle || '',
                      htmlDescription: fallbackMeta.htmlDescription || '',
                      categories: [] // Categories are locale-specific, so start with empty array
                    });
                  }
                })
              );
            })
          );
        }

        this.$swal.fire({
          icon: 'success',
          title: 'Selected aliases updated',
          timer: 1500,
          showConfirmButton: false
        });
        this.bulkAliasValue = '';
        this.selectedGameAliases = [];
        this.selectedFeedGames = [];
        await this.loadFeedAliases(); // Reload aliases after update
      } catch (e) {
        this.$swal.fire({
          icon: 'error',
          title: e.message,
          timer: 1500,
          showConfirmButton: false
        });
      }
    },
    async clearSelectedAliases() {
      // Show confirmation dialog
      const result = await this.$swal.fire({
        title: 'Clear Selected Aliases?',
        text: `This will remove aliases from ${this.selectedGameAliases.length + this.selectedFeedGames.length} selected items. This action cannot be undone.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, clear them!',
        cancelButtonText: 'Cancel'
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        // Clear selected game level aliases
        if (this.selectedGameAliases.length > 0) {
          const metas = await this.$gameApi.get(`/games/${this.item.id}/metas`);
          await Promise.all(
            metas.map(async (meta) => {
              await this.$gameApi.patch(`/games/${this.item.id}/metas/${meta.id}`, {
                ...meta,
                alias: null
              });
            })
          );
        }

        // Clear selected feed level aliases
        if (this.selectedFeedGames.length > 0) {
          await Promise.all(
            this.selectedFeedGames.map(async (item) => {
              const metas = await this.$gameApi.get(`/feeds/${item.feedId}/games/${this.item.id}/metas`);
              await Promise.all(
                metas.map(async (meta) => {
                  await this.$gameApi.patch(`/feeds/${item.feedId}/games/${this.item.id}/metas/${meta.id}`, {
                    ...meta,
                    alias: null
                  });
                })
              );
            })
          );
        }

        this.$swal.fire({
          icon: 'success',
          title: 'Selected aliases cleared',
          timer: 1500,
          showConfirmButton: false
        });
        this.selectedGameAliases = [];
        this.selectedFeedGames = [];
        await this.loadFeedAliases(); // Reload aliases after update
      } catch (e) {
        this.$swal.fire({
          icon: 'error',
          title: e.message,
          timer: 1500,
          showConfirmButton: false
        });
      }
    },
    filterFeedGames(value, search, item) {
      if (!search) return true;
      const searchTerm = search.toLowerCase();

      // Check feed name
      if ((item.feed?.name || '').toLowerCase().includes(searchTerm)) {
        return true;
      }

      // Check all locale aliases
      return gameLocales.some(locale => {
        const alias = item[`alias_${locale.value}`];
        return alias && alias.toLowerCase().includes(searchTerm);
      });
    },

    validateRequiredFields() {
      const hasSlug = !!(this.item.slug && String(this.item.slug).trim());
      const hasName = !!(this.item.name && String(this.item.name).trim());
      const hasRenderingType = !!this.item.renderingType;
      const hasEngine = !!(this.item.engine && String(this.item.engine).trim());
      const hasDeveloper = !!this.item.developerId;

      return hasSlug && hasName && hasRenderingType && hasEngine && hasDeveloper;
    },

    save() {
      if (this.$refs.form && !this.$refs.form.validate()) {
        return;
      }
      if (!this.validateRequiredFields()) {
        return;
      }

      this.item.isSubscriberOnlyDate = this.item.isSubscriberOnlyDate === '' ? null : this.item.isSubscriberOnlyDate;
      this.item.comingSoonDate = this.item.comingSoonDate === '' ? null : this.item.comingSoonDate;

      // Ensure gamePrerollSettings is properly set before saving
      if (this.gamePrerollSettings) {
        this.item.gamePrerollSettings = { ...this.gamePrerollSettings };
      }

      // Ensure supportInfo is properly set before saving
      if (this.supportInfo) {
        this.item.supportInfo = { ...this.supportInfo };
      }

      this.$emit('save', this.item);
    },
    updateIframeAllowOptions() {
      // Update the textarea when checkboxes change
      this.item.iframeAllowOptions = this.selectedDirectives.join('; ');
    },

    parseIframeAllowOptions() {
      // Parse the textarea value and update checkboxes
      if (this.item.iframeAllowOptions) {
        this.selectedDirectives = this.item.iframeAllowOptions.split(';').map(d => d.trim()).filter(d => d);
      } else {
        this.selectedDirectives = [];
      }
    }
  }
};
</script>

<style scoped lang="scss">
.game-form {
  width: 100%;
}

.game-form__tabs-shell {
  border: 1px solid rgba(0, 0, 0, 0.22);
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  margin-bottom: 16px;
}

.game-form__tabs {
  border: none !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
  border-radius: 0 !important;
  background: #f5f5f5 !important;
  margin: 0 !important;
}

.game-form__tab-panel {
  margin: 0;
  border: none;
  border-radius: 0;
  background: #fff;
  min-height: 200px;
  box-shadow: none;
}

.game-form__tab-panel-body {
  padding: 0;
  overflow-x: auto;
}

.game-form__tab-panel-body--padded {
  padding: 16px;
}

.game-form__tab-panel-body > .mt-2 {
  margin-top: 0 !important;
  padding: 16px;
}

.game-form__tab-panel-body ::v-deep > .v-card {
  margin: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  border: none !important;
}

.game-form__section {
  border-left: 4px solid #1976d2;
  overflow: hidden;
}

.game-form__section-title {
  display: flex;
  align-items: center;
  padding-top: 16px !important;
  padding-bottom: 12px !important;
  font-size: 1.15rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.01em;
}

.game-form__section ::v-deep > .v-card-text {
  padding-top: 4px !important;
  padding-bottom: 12px !important;
}

/* Space for floating labels when hide-details removes the default messages gap */
.game-form__section ::v-deep .row--dense > .col,
.game-form__section ::v-deep .row--dense > [class*="col-"] {
  padding-top: 12px !important;
  padding-bottom: 12px !important;
}

.game-form__field-with-action {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  min-width: 0;
}

.game-form__field-with-action-input {
  flex: 1 1 180px;
  min-width: 0;
}

.game-form__field-with-action-btn {
  flex: 0 0 auto;
}

.game-form__flags {
  gap: 4px 8px;
}

.game-form__nested {
  padding: 16px;
  border: 1px solid #1976d2;
  border-radius: 8px;
  background: #fff;
}

.game-form__nested-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1565c0;
  margin-bottom: 12px;
}

.game-form__save {
  display: flex;
  justify-content: flex-start;
  position: sticky;
  bottom: 12px;
  z-index: 2;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(25, 118, 210, 0.2);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(6px);
}

.game-tab-slide-enter-active,
.game-tab-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.game-tab-slide-enter {
  opacity: 0;
  transform: translateX(24px);
}

.game-tab-slide-leave-to {
  opacity: 0;
  transform: translateX(-24px);
}
</style>
